import React, {useState, useEffect, useMemo} from "react";

import Box from '@mui/material/Box';

import "../App.css"
import {useAppCtx} from "../app/AppCtx";
import {useCrewCtx} from "../app/CrewCtx";
import {PageHeader} from "./PageHeader";
import {CxDataGrid} from "../cmx/CxData";
import {CxGrid} from "../cmx/CxGrid";
import {CxListFeedback} from "../cmx/CxFeedback";
import {CxText} from "../cmx/CxTypography"
import {CxLocalization, CxTextInput, CxComboxInput, CxDateInput, CxResetFilterButton} from "../cmx/CxForm";
import {CxReportDialog} from "../cmx/CxReport";
import {errorStatus, pendingStatus, successStatus} from "../ctx/AppStatus";
import {Project} from "../domain/Project";
import {Report} from "../domain/Report";
import {initialCall, runningCall, successCall} from "../gateway/CallService";
import {projectViewList} from "../gateway/ProjectService"
import {DateUtil} from "../lib/DateUtil";


export function ProjectListAvor() {

    const {handleAction, getPageState, setPageState, updateAppStatus} = useAppCtx();
    const {crew, lazyloadCrews} = useCrewCtx();

    const pageId  = 'ProjectListAvor';      // id used for PageState and PageData
    const viewKey = 'view';                 // PageState

    useEffect(() => {
        lazyloadCrews();
    }, []);

    // ----------------------------------------------------------------------------------------------------------------
    // header and view handling
    // ----------------------------------------------------------------------------------------------------------------

    const [report, setReport] = useState(null);     // values: [null | 'avor']

    const viewConfig = {
        openView: {
            title : "Projekte mit offenem Vorausmass",
            desc  : "Anzeige aller laufenden Projekte, bei denen das Vorausmass noch nicht festgelegt wurde.",
            query : "projectAvorOpenList"
        },
        noView: {
            title : "Projekte ohne Vorausmass",
            desc  : "Anzeige aller laufenden Projekte, bei denen bewusst kein Vorausmass erstellt wird.",
            query : "projectAvorNoList"
        },
        pendingView: {
            title : "Projekte mit pendenten Vorausmass ",
            desc  : "Anzeige aller laufenden Projekte, für die ein Vorausmass zu berechnen ist.",
            query : "projectAvorPendingList"
        },
        doneView: {
            title : "Projekte mit erfassten Vorausmass ",
            desc  : "Anzeige aller laufenden Projekte, für die ein Vorausmass erfasst wurde.",
            query : "projectAvorDoneList"
        },
    };

    const view = getPageState(pageId, viewKey);
    const cfg  = viewConfig[view];

    const handleViewChange = (value) => {
        setPageState(pageId, viewKey, value);
        setCall(initialCall());
    };

    const handleLocalActionChange = (value) => {
        if (value === "openAvorReport") {
            console.log("setReport: ", Report.AVOR)
            setReport(Report.AVOR);
        }
    };

    const viewItems = [
        { label: "offen",    value: "openView" },
        { label: "nein",     value: "noView" },
        { label: "pendent",  value: "pendingView" },
        { label: "erfasst",  value: "doneView" },
    ];

    const localActionItems= [
        { kind: "tooltip", label: "Vorausmassliste",  iconName: "avorReport", actionName: "openAvorReport" }
    ]


    const actionItems = [
        { kind: "tooltip", label: "Zurück zu Projekte", iconName: "project", actionName: "project" },
        { kind: "tooltip", label: "Projekte suchen",    iconName: "search",  actionName: "projectSearch" }
    ];

    const renderHeader = () => {
        return (
            <>
                <PageHeader
                    title={cfg.title}
                    viewItems={viewItems}
                    viewInitialValue={view}
                    viewOnChangeHandler={handleViewChange}
                    localActionItems={localActionItems}
                    localActionOnChangeHandler={handleLocalActionChange}
                    actionItems={actionItems}
                />
                <CxGrid size="xl">
                    <CxText>{cfg.desc}</CxText>
                </CxGrid>
            </>
        );
    }

    // ----------------------------------------------------------------------------------------------------------------
    // control panel
    // ----------------------------------------------------------------------------------------------------------------

    const projectRef  = getPageState(pageId, 'projectRef');
    const projectDesc = getPageState(pageId, 'projectDesc');
    const foreman     = getPageState(pageId, 'foreman');
    const deputy      = getPageState(pageId, 'deputy');
    const avorBy      = getPageState(pageId, 'avorBy');
    const avorFrom    = getPageState(pageId, 'avorFrom');
    const avorTo      = getPageState(pageId, 'avorTo');

    const handleFilterReset= () => {
        const defaults = {
            projectRef  : '',
            projectDesc : '',
            foreman     : -1,
            deputy      : -1,
            avorBy      : -1,
            avorFrom    : null,
            avorTo      : null
        };

        Object.keys(defaults).forEach(field => {
            setPageState(pageId, field, defaults[field]);
        });
    }

    const handleFilterChange = (field, value) => {
        setPageState(pageId, field, value);
    }

    const renderTextFilter = (field, label, value, maxLength, numericOnly=false) => {
        return (
            <CxTextInput id={field} label={label} value={value} maxLength={maxLength} numericOnly={numericOnly}
                         handleChange={(value) => handleFilterChange(field, value)} />
        );
    }

    const renderDateFilter = (field, label, value) => {
        return (
            <CxDateInput id={field} label={label} value={value}
                         handleChange={(value) => handleFilterChange(field, value)} />
        )
    }

    const renderComboxFilter = (field, label, value, items) => {
        return (
            <CxComboxInput
                id={field}
                name={label}
                value={value}
                onChange={(value) => handleFilterChange(field, value)}
                items={items}
            />
        );
    }

    const renderCtrl = () => {
        return (
            <CxLocalization>

                <CxGrid size="lg">
                    {renderTextFilter('projectRef', 'Projekt Nr', projectRef, 64)}
                </CxGrid>
                <CxGrid size="lg">
                    {renderTextFilter('projectDesc', 'Projekt Beschreibung', projectDesc, 1024)}
                </CxGrid>

                <CxGrid size="lg">
                    {renderComboxFilter('foreman', 'Bauführer', foreman, crew.getForemanSelection(true,"Alle"))}
                </CxGrid>
                <CxGrid size="lg">
                    {renderComboxFilter('deputy', 'Bauführer Stellvertreter', deputy, crew.getForemanSelection(true,"Alle"))}
                </CxGrid>

                <CxGrid size="sm">
                    {renderDateFilter('avorFrom', 'Vorausmass erstellt ab', avorFrom)}
                </CxGrid>
                <CxGrid size="sm">
                    {renderDateFilter('avorTo', 'Vorausmass erstellt bis', avorTo)}
                </CxGrid>
                <CxGrid size="sm">
                    {renderComboxFilter('avorBy', 'Vorausmass erstellt von', avorBy, crew.getAvorSelection(true, "Alle"))}
                </CxGrid>
                <CxGrid size="sm">
                    <CxResetFilterButton handleSubmit={handleFilterReset} />
                </CxGrid>

            </CxLocalization>
        );
    }

    // ----------------------------------------------------------------------------------------------------------------
    // select view data
    // ----------------------------------------------------------------------------------------------------------------

    const [call, setCall] = useState(runningCall());

    useEffect(() => {
        // TODO MAINTENANCE: add active flag and useEffect response
        setCall(runningCall())
        updateAppStatus('call', pendingStatus("Project data load is pending."));

        projectViewList(cfg.query).then(responseCall => {
            if (responseCall.isSuccess) {
                const mappedRows = Project.mapListColumns(responseCall.result, crew);
                setCall(successCall(mappedRows));
                updateAppStatus('call', successStatus(`Project data ${responseCall.result.length} records successfully loaded.`, responseCall.elapsed));
            } else {
                setCall(responseCall);
                updateAppStatus('call', errorStatus(`Project data load failed with error: ${responseCall.result}`, responseCall.elapsed));
            }
        });

    }, [view]);

    // ----------------------------------------------------------------------------------------------------------------
    // filter data
    // ----------------------------------------------------------------------------------------------------------------

    const rows = useMemo(() => {
        if (call.isSuccess) {

            return call.result.filter((project) => {

                // text filter (case Insensitive & partial hits)
                if (projectRef && !project.project_ref?.toLowerCase().includes(projectRef.toLowerCase())) {
                    return false;
                }
                if (projectDesc && !project.project_desc?.toLowerCase().includes(projectDesc.toLowerCase())) {
                    return false;
                }

                // dropdown filter
                if (foreman !== -1 && project.foreman_crew_id !== foreman) {
                    return false;
                }
                if (deputy !== -1 && project.deputy_foreman_crew_id !== deputy) {
                    return false;
                }
                if (avorBy !== -1 && project.avor_created_crew_id !== avorBy) {
                    return false;
                }

                // date filter (iso format)
                if (avorFrom) {
                    const avorFromIso = DateUtil.convertDateToIsoString(avorFrom);
                    if (project.avor_created_at < avorFromIso) return false;
                }
                if (avorTo) {
                    const avorToIso = DateUtil.convertDateToIsoString(avorTo);
                    if (project.avor_created_at > avorToIso) return false;
                }
                return true;
            });
        }
        return [];

    }, [call, cfg, projectRef, projectDesc, foreman, deputy, avorBy, avorFrom, avorTo]);


    // ----------------------------------------------------------------------------------------------------------------
    // render view
    // ----------------------------------------------------------------------------------------------------------------

    const handleRowDoubleClick = (params) => {
        handleAction("projectDetail", params.row);
    };

    const handleEditClick = (params) => {
        handleAction("projectUpdate", params.row, "projectAvor");       // return back to 'project' view after edit
    }

    const renderRows = () => {
        return (
            <CxGrid size="xl">
                <CxDataGrid
                    rows={rows}
                    columns={Project.getListAvorColumns()}
                    onRowDoubleClick={handleRowDoubleClick}
                    onViewAction={handleRowDoubleClick}
                    onEditAction={handleEditClick} />
            </CxGrid>
        )
    }

    const reportInfo = () => {
        const parts = [];

        if (projectRef)         parts.push(`Projekt Nr: *${projectRef}*`);
        if (projectDesc)        parts.push(`Projekt Beschreibung: *${projectDesc}*`);
        if (foreman  !== -1)    parts.push(`Bauführer: ${crew.getName(foreman)}`);
        if (deputy   !== -1)    parts.push(`Bauführer Stellvertreter: ${crew.getName(deputy)}`);
        if (avorBy   !== -1)    parts.push(`Erstellt von: ${crew.getName(avorBy)}`);
        if (avorFrom !== null)  parts.push(`Erstellt ab: ${DateUtil.convertDateToString(avorFrom)}`);
        if (avorTo   !== null)  parts.push(`Erstellt bis: ${DateUtil.convertDateToString(avorTo)}`);

        return parts.length > 0 ? `${parts.join(', ')}` : null;
    }

    const renderReport = () => {
        const reportTitle = cfg.title;                                        // view title instead of Report.getName(report)
        const reportRows = Report.getReportRows(report, null, rows);    // no codes needed

        return (
            <CxReportDialog reportType={report} title={reportTitle} rows={reportRows}
                            open={!!report} onClose={() => setReport(null)} info={reportInfo()} />
        )
    }


    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                { renderHeader() }
                { renderCtrl() }

                <CxListFeedback call={call} />

                {call.isSuccess && (
                    <>
                        {report && renderReport()}
                        {!report && renderRows()}
                    </>
                )}
            </CxGrid>
        </Box>
    );
}


// --------------------------------------------------------------------------------------------------------------------
// The end.